import { useState } from 'react';
import { C, SectionLabel, wrap, BtnOrange, BtnNavy } from './styles';
import { UL, LI } from './content/Typography';
import { useDocumentMeta } from './content/useDocumentMeta';
import { useNoindex } from './content/useNoindex';
import Nav from './components/Nav';
import Footer from './components/Footer';

const PAGE_TITLE = 'Admin: Re-render Prompt Tool | MetalMatch';
const PAGE_DESCRIPTION = 'Internal tool for generating revised AI rendering prompts.';

const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE;

function stripTrailingPeriod(s) {
  return s.replace(/\.+\s*$/, '');
}

function buildRevisedPrompt(original, changes) {
  const cleanOriginal = stripTrailingPeriod(original);
  const cleanChanges = stripTrailingPeriod(changes);
  return `Based on the original prompt: ${cleanOriginal}. Apply these modifications: ${cleanChanges}. Generate a photorealistic product rendering on a dark metallic studio background with professional lighting. The part should appear as a finished manufactured metal component.`;
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 10, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function PasscodeGate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [incorrect, setIncorrect] = useState(false);

  const handleSubmit = () => {
    if (value === ADMIN_PASSCODE) {
      onUnlock(value);
    } else {
      setIncorrect(true);
    }
  };

  return (
    <>
      <Nav />
      <section style={{ padding: '120px 0', minHeight: '55vh', display: 'flex', alignItems: 'center' }}>
        <div style={{ ...wrap, maxWidth: 360 }}>
          <SectionLabel orange>Internal tool</SectionLabel>
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(24px, 4vw, 32px)',
            fontWeight: 700, lineHeight: 1.1,
            textTransform: 'uppercase', color: C.txt,
            marginBottom: 18,
          }}>Enter admin passcode</h1>
          <input
            type="password"
            value={value}
            onChange={(e) => { setValue(e.target.value); setIncorrect(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            placeholder="Passcode"
            autoComplete="off"
            autoFocus
            style={{ marginBottom: 14 }}
          />
          <BtnOrange onClick={handleSubmit} style={{ width: '100%', justifyContent: 'center', padding: 15 }}>
            Unlock
          </BtnOrange>
          {incorrect && (
            <p style={{ fontSize: 13, color: C.orange, marginTop: 12 }}>Incorrect passcode.</p>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

export default function AdminRerender() {
  useDocumentMeta(PAGE_TITLE, PAGE_DESCRIPTION);
  useNoindex();

  const [unlocked, setUnlocked] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [recordId, setRecordId] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [changes, setChanges] = useState('');
  const [revisedPrompt, setRevisedPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [renderUrl, setRenderUrl] = useState('');
  const [generatingRender, setGeneratingRender] = useState(false);
  const [renderError, setRenderError] = useState('');

  const canGenerate = originalPrompt.trim() !== '' && changes.trim() !== '';
  const canGenerateRender = revisedPrompt.trim() !== '' && apiKey.trim() !== '' && !generatingRender;

  const handleGenerate = () => {
    if (!canGenerate) return;
    setRevisedPrompt(buildRevisedPrompt(originalPrompt.trim(), changes.trim()));
    setCopied(false);
    setRenderUrl('');
    setRenderError('');
  };

  const handleCopy = () => {
    if (!revisedPrompt) return;
    navigator.clipboard.writeText(revisedPrompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleGenerateRender = async () => {
    if (!canGenerateRender) return;
    setGeneratingRender(true);
    setRenderError('');
    setRenderUrl('');
    try {
      const res = await fetch('/.netlify/functions/generate-render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: revisedPrompt,
          apiKey: apiKey.trim(),
          passcode,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }
      if (!data?.url) throw new Error('No image was returned.');
      setRenderUrl(data.url);
    } catch (err) {
      setRenderError(err.message || 'Something went wrong generating the render.');
    } finally {
      setGeneratingRender(false);
    }
  };

  const handleDownload = async () => {
    if (!renderUrl) return;
    try {
      const res = await fetch(renderUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `metalmatch-render-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(renderUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!unlocked) {
    return <PasscodeGate onUnlock={(pw) => { setPasscode(pw); setUnlocked(true); }} />;
  }

  return (
    <>
      <Nav />

      <section style={{ padding: '64px 0 20px', borderBottom: `1px solid ${C.bdr}` }}>
        <div style={wrap}>
          <SectionLabel orange>Internal tool</SectionLabel>
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(28px, 5vw, 44px)',
            fontWeight: 700, lineHeight: 1.08,
            textTransform: 'uppercase', color: C.txt,
            maxWidth: 700, marginBottom: 16,
          }}>AI Re-render Prompt Generator</h1>
          <p style={{ color: C.mut, fontSize: 15, lineHeight: 1.7, maxWidth: 600 }}>
            When a concept rendering doesn't match a customer's sketch, use this to combine the original prompt with the requested
            changes into a clean, re-usable prompt for generating a corrected render.
          </p>
        </div>
      </section>

      <section style={{ padding: '40px 0 8px' }}>
        <div style={{ ...wrap, maxWidth: 720 }}>

          <div style={{ background: C.surf, border: `1px solid ${C.bdr2}`, borderRadius: 10, padding: 24, marginBottom: 16 }}>
            <FieldLabel>OpenAI API Key</FieldLabel>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              autoComplete="off"
            />
            <p style={{ fontSize: 11, color: C.dim, marginTop: 8, lineHeight: 1.6 }}>
              Kept in memory for this session only — never saved to your browser. Sent to our serverless function to generate the render, which forwards it to OpenAI and does not store it.
            </p>
          </div>

          <div style={{ background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 24, marginBottom: 16 }}>
            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Airtable Record ID</FieldLabel>
              <input
                value={recordId}
                onChange={(e) => setRecordId(e.target.value)}
                placeholder="e.g. recAbC123xyz"
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <FieldLabel>Original AI Prompt</FieldLabel>
              <textarea
                value={originalPrompt}
                onChange={(e) => setOriginalPrompt(e.target.value)}
                placeholder="Paste the prompt that was originally used to generate this render."
                style={{ minHeight: 110, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <FieldLabel>What Needs to Change</FieldLabel>
              <textarea
                value={changes}
                onChange={(e) => setChanges(e.target.value)}
                placeholder='e.g. "bracket should have 4 holes not 2, base is too narrow, needs to be L-shaped not flat"'
                style={{ minHeight: 110, resize: 'vertical' }}
              />
            </div>

            <BtnOrange
              onClick={handleGenerate}
              style={{ width: '100%', justifyContent: 'center', padding: 15, opacity: canGenerate ? 1 : 0.5, cursor: canGenerate ? 'pointer' : 'not-allowed' }}
            >
              Generate Revised Prompt
            </BtnOrange>
          </div>

          <div style={{ background: C.surf2, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 24, marginBottom: 16 }}>
            <FieldLabel>Revised Prompt</FieldLabel>
            <textarea
              value={revisedPrompt}
              readOnly
              placeholder="Your revised prompt will appear here after you click Generate Revised Prompt."
              style={{ minHeight: 140, resize: 'vertical', marginBottom: 14 }}
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <BtnNavy onClick={handleCopy} style={{ opacity: revisedPrompt ? 1 : 0.5, cursor: revisedPrompt ? 'pointer' : 'not-allowed' }}>
                {copied ? 'Copied ✓' : 'Copy to Clipboard'}
              </BtnNavy>
              <a
                href="https://platform.openai.com/playground"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: "'Instrument Sans', sans-serif", fontSize: 13, fontWeight: 600,
                  padding: '13px 26px', borderRadius: 5, border: `1.5px solid ${C.bdr2}`,
                  color: C.mut, textDecoration: 'none',
                }}
              >
                Open OpenAI Playground →
              </a>
            </div>

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.bdr}` }}>
              <BtnOrange
                onClick={handleGenerateRender}
                style={{ width: '100%', justifyContent: 'center', padding: 15, opacity: canGenerateRender ? 1 : 0.5, cursor: canGenerateRender ? 'pointer' : 'not-allowed' }}
              >
                {generatingRender ? 'Generating…' : 'Generate New Render'}
              </BtnOrange>
              {!apiKey.trim() && (
                <p style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Enter your OpenAI API key above to generate a render.</p>
              )}
              {renderError && (
                <p style={{ fontSize: 12, color: C.orange, marginTop: 10 }}>{renderError}</p>
              )}

              {renderUrl && (
                <div style={{ marginTop: 18 }}>
                  <img
                    src={renderUrl}
                    alt="AI-generated concept rendering"
                    style={{ width: '100%', borderRadius: 8, border: `1px solid ${C.bdr}`, display: 'block', marginBottom: 12 }}
                  />
                  <BtnNavy onClick={handleDownload} style={{ width: '100%', justifyContent: 'center' }}>
                    Download Render
                  </BtnNavy>
                </div>
              )}
            </div>
          </div>

          <div style={{ background: C.navybg, border: `1px solid rgba(50,151,219,0.3)`, borderRadius: 8, padding: '20px 22px' }}>
            <div style={{ fontSize: 10, color: C.navy, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Tips</div>
            <p style={{ fontSize: 13, color: C.mut, marginBottom: 12 }}>Common revision patterns:</p>
            <UL>
              <LI>Add "exactly N holes" for hole count issues.</LI>
              <LI>Add "L-shaped / U-shaped / flat" for shape issues.</LI>
              <LI>Add specific dimensions like "150mm x 200mm".</LI>
              <LI>Add "viewed from above / front / side" for angle issues.</LI>
            </UL>
          </div>

        </div>
      </section>

      <div style={{ height: 64 }} />
      <Footer />
    </>
  );
}
